import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { AdjustmentType, ComboType, OrderItem } from '@oolio-group/domain';
import { useCurrency, useTranslation } from '@oolio-group/localization';
import { isDefaultModifierRemoved } from '@oolio-group/client-utils';
import {
  computeOrderItemTotal,
  getAdjustmentLabel,
} from '@oolio-group/order-helper';
import { useTheme } from '../../../hooks/useTheme';
import { getStyles } from './CartItem.styles';

interface Props {
  item: OrderItem;
}

const CartItem: React.FC<Props> = ({ item }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);
  const { formatCurrency } = useCurrency();
  const { translate } = useTranslation();

  const renderModifiers = (orderItem: OrderItem, isCombo = false) => {
    return orderItem.modifiers
      ?.filter(modifier => !isDefaultModifierRemoved(modifier))
      .map(modifier => {
        const comboProductQuantity = isCombo ? orderItem.quantity ?? 1 : 1;

        const sum =
          modifier.unitPrice *
          ((modifier?.quantity ?? 1) *
            comboProductQuantity *
            (item.quantity ?? 1));

        return (
          <View key={modifier.id} style={styles.row}>
            <Text testID="order-item-modifiers-name" style={styles.labelMinor}>
              {modifier.quantity ?? 1} × {modifier.name}
            </Text>
            {sum > 0 && (
              <Text
                testID="order-item-modifiers-price"
                style={styles.valueMinor}
              >
                {formatCurrency(sum)}
              </Text>
            )}
          </View>
        );
      });
  };

  const renderComboItem = () => {
    return item?.comboItems?.map(comboItem => {
      const sum =
        (comboItem.unitPrice ?? 0) *
        ((comboItem?.quantity ?? 1) * (item.quantity ?? 1));
      return (
        <View key={comboItem.id} style={styles.comboItem}>
          <View style={styles.row}>
            <Text testID="order-item-combo" style={styles.label}>
              {item.product?.comboType === ComboType.PORTION
                ? comboItem.product?.name
                : `${comboItem?.quantity ?? 1} × ${comboItem.product?.name}`}
            </Text>
            <Text testID="order-item-modifiers-price" style={styles.value}>
              {formatCurrency(sum)}
            </Text>
          </View>
          {!!comboItem.notes && (
            <Text testID="combo-item-notes" style={styles.note}>
              {translate('cdsCartScreen.itemNote', {
                note: comboItem.notes.trim(),
              })}
            </Text>
          )}
          {renderModifiers(comboItem as OrderItem, true)}
        </View>
      );
    });
  };

  const renderAdjustments = (
    adjustmentAmount: number,
    label: string,
    testID: string,
  ) => {
    return (
      typeof adjustmentAmount === 'number' &&
      adjustmentAmount > 0 && (
        <View style={styles.row}>
          <Text style={styles.labelMinor}>{label}</Text>
          <Text testID={testID} style={styles.valueMinor}>
            {formatCurrency(adjustmentAmount)}
          </Text>
        </View>
      )
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <Text testID="order-item-name-quantity" style={styles.label}>
          {`${item?.quantity > 1 ? `${item?.quantity} × ` : ''}${
            !!item.variant ? item.variant?.name : item.product?.name
          }`}
        </Text>
        <Text testID="order-item-price" style={styles.value}>
          {formatCurrency(computeOrderItemTotal(item))}
        </Text>
      </View>
      {!!item.notes && (
        <Text testID="order-item-notes" style={styles.note}>
          {translate('cdsCartScreen.itemNote', { note: item.notes.trim() })}
        </Text>
      )}
      {!!item.variant && (
        <Text testID="order-item-variant" style={styles.labelMinor}>
          {item.product.name.replace(`${item.variant.name}-`, '')}
        </Text>
      )}
      {renderModifiers(item)}
      {renderComboItem()}
      {!!item.savedAmount && item.savedAmount > 0 && (
        <Text style={styles.savings}>
          {translate('cdsCartScreen.saveMessage', {
            savedAmount: formatCurrency(
              item?.savedAmount * (item?.quantity ?? 1),
            ),
            productName: item.product?.name,
          })}
        </Text>
      )}
      {renderAdjustments(
        item.discountAmount,
        getAdjustmentLabel(
          item.adjustments?.find(
            a => a?.adjustmentType === AdjustmentType.DISCOUNT,
          ),
        ) || translate('order.discount'),
        'order-item-discount',
      )}
      {renderAdjustments(
        item.surchargeAmount || 0,
        getAdjustmentLabel(
          item.adjustments?.find(
            a => a?.adjustmentType === AdjustmentType.SURCHARGE,
          ),
        ) || translate('order.surcharge'),
        'order-item-surcharge',
      )}
    </View>
  );
};

export default CartItem;
